<template>
  <div class="audio-card">
    <div class="audio-thumbnail">
      <img :src="audio.imgMini ? bsUrl + 'images/' + audio.imgMini : defaultThumbnail" />

      <div class="audio-title">{{ audio.text }}</div>

      <div class="playing-overlay" v-if="isPlaying">
        <img src="./img/anim.svg" class="sound-wave-svg" />
      </div>

      <button v-if="$store.state.user.uid === audio.userId && editButtonEnable" 
              class="edit-button" 
              @click.stop="$store.commit('setModifyPublication', audio)"
              >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>

      <div class="duration-badge">{{ formatDuration(audio.duration) }}</div>

      <div class="play-trigger" @click.stop="togglePlay">
        <svg viewBox="0 0 24 24" fill="currentColor" v-if="!isPlaying">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg viewBox="0 0 24 24" fill="currentColor" v-else>
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
        </svg>
      </div>

      <div class="views-counter">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        {{ formatViews(audio.stats?.views) }}
      </div>
    </div>

    <!-- Progress Bar -->
    <progress class="progress-bar" :value="progress" max="100"></progress>
  </div>
</template>

<script>
import audioService from './audioService.js'

export default {
  name: 'AudioCard',

  props: {
    audio: {
      type: Object,
      required: true,
    },
    editButtonEnable: {
      type:Boolean,
      default:true
    }
  },

  data() {
    return {
      bsUrl: this.$store.state.baseUrl,
      defaultThumbnail: require('./img/publication.svg'),
      isPlaying: false,
      progress:  0,
    }
  },

  computed: {
    // Stable unique id for this card — used as the audioService key
    cardId() {
      return this.audio._id || this.audio.audio || String(Math.random())
    },
    audioUrl() {
      return this.audio.audio ? this.bsUrl + 'audios/' + this.audio.audio : null
    },
  },

  methods: {
    togglePlay() {
      if (!this.audioUrl) return

      if (this.isPlaying) {
        audioService.pause(this.cardId)
        this.isPlaying = false
        this.$store.commit('setCurrentPlayer', null)
      } else {
        audioService.play(
          this.cardId,
          this.audioUrl,
          // onProgress
          (pct) => { this.progress = pct },
          // onEnd
          () => {
            this.isPlaying = false
            this.progress  = 0
            this.$emit('playingEnded')
            this.$store.commit('setCurrentPlayer', null)
          }
        )
        this.isPlaying = true
        this.$emit('play', this.cardId)
        this.$store.commit('setCurrentPlayer', 'audioCard')
      }
    },

    formatViews(views) {
      if (!views) return 0
      if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M'
      if (views >= 1000)    return (views / 1000).toFixed(1) + 'K'
      return views.toString()
    },

    formatDuration(seconds) {
      if (!seconds)       return '0s'
      if (seconds < 60)   return Math.floor(seconds) + 's'
      if (seconds < 3600) return Math.floor(seconds / 60) + 'm'
      return Math.floor(seconds / 3600) + 'h'
    },
  },
  watch : {
    '$store.state.currentPlayer' (newVal) {
      if (newVal !== 'audioCard' && this.isPlaying) {
        audioService.pause(this.cardId)
        this.isPlaying = false
        this.$store.commit('setCurrentPlayer', null)
      }
    }
  },
  mounted() {
    // Register forced-stop callback so this card's UI resets
    // when another card starts playing
    audioService.onForcedStop(this.cardId, () => {
      this.isPlaying = false
      this.progress  = 0
    })
  },

  beforeUnmount() {
    // Release the service slot for this card — stops audio if it was active
    audioService.destroy(this.cardId)
  },
}
</script>

<style scoped>
.audio-card {
  max-width: 200px;
  width: 25%;
  height: 230px;
  position: relative;
  aspect-ratio: 9 / 16;
  overflow: hidden;
  cursor: pointer;
  background: #000;
  border-left: solid 1px rgba(192, 192, 192, 0.1);
}

.audio-thumbnail {
  width: 100%;
  height: 100%;
  position: relative;
}

.audio-thumbnail > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.playing-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sound-wave-svg {
  width: 60px;
  height: 40px;
}

.edit-button {
  position: absolute;
  top: 8px; left: 8px;
  width: 32px; height: 32px;
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid rgba(0, 212, 255, 0.4);
  border-radius: 6px;
  color: #00d4ff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 0;
}
.edit-button svg { width: 18px; height: 18px; }

.audio-title {
  position: absolute;
  bottom: 50px; left: 8px; right: 8px;
  color: #ffffff;
  font-size: 13px; font-weight: 700;
  text-shadow: 0 2px 10px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.8);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 65px;
  z-index: 5;
  word-break: break-word;
}

.duration-badge {
  position: absolute;
  top: 8px; right: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #ffffff;
  font-size: 12px; font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
}

.play-trigger {
  position: absolute;
  bottom: 8px; left: 8px;
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.15s;
}
.play-trigger svg    { width: 20px; height: 20px; }
.play-trigger:hover  { transform: scale(1.15); }

.views-counter {
  position: absolute;
  bottom: 10px; left: 45px;
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 13px; font-weight: 600;
  text-shadow: 0 2px 4px rgba(0,0,0,0.8);
}
.views-counter svg {
  width: 16px; height: 16px;
  margin-right: 4px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.8));
}
.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 3px;
  appearance: none;
  -webkit-appearance: none;
  border: none; /* Add this */
  padding: 0; /* Add this */
  background: rgba(255, 255, 255, 0.3);
}

.progress-bar::-webkit-progress-bar {
  background: rgba(255, 255, 255, 0.3);
}

.progress-bar::-webkit-progress-value {
  background: #00d4ff;
}

.progress-bar::-moz-progress-bar {
  background: #00d4ff;
}

@media (max-width: 1024px) { .audio-card { width: 25%; } }
@media (max-width: 768px)  { .audio-card { width: 33.33%; } }
@media (max-width: 350px)  { .audio-card { width: 50%; } }
</style>